/**
 * Lightweight client-side polish for Hyper Dusk night stills.
 * Tone / white-balance / centre-crop subset of PhotoEdit's render pipeline.
 */

export interface PolishTone {
  exposure: number;
  brightness: number;
  contrast: number;
  highlights: number;
  shadows: number;
}

export interface PolishColour {
  temperature: number;
  tint: number;
  saturation: number;
}

export type CropAspect = 'free' | '16:9' | '9:16' | '1:1' | '4:3';

export interface PolishState {
  tone: PolishTone;
  colour: PolishColour;
  cropAspect: CropAspect;
}

export const DEFAULT_POLISH: PolishState = {
  tone: { exposure: 0, brightness: 0, contrast: 0, highlights: 0, shadows: 0 },
  colour: { temperature: 0, tint: 0, saturation: 0 },
  cropAspect: 'free',
};

export const CROP_ASPECT_OPTIONS: Array<{ id: CropAspect; label: string; ratio: number | null }> = [
  { id: 'free', label: 'Original', ratio: null },
  { id: '16:9', label: '16:9', ratio: 16 / 9 },
  { id: '9:16', label: '9:16', ratio: 9 / 16 },
  { id: '1:1', label: '1:1', ratio: 1 },
  { id: '4:3', label: '4:3', ratio: 4 / 3 },
];

const clamp01 = (v: number) => (v < 0 ? 0 : v > 1 ? 1 : v);
const clamp255 = (v: number) => (v < 0 ? 0 : v > 255 ? 255 : Math.round(v));

export async function loadImage(url: string): Promise<HTMLImageElement> {
  // Prefer fetch→blob so canvas export isn't blocked by storage CORS.
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`Failed to fetch image (${res.status})`);
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    return await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        reject(new Error('Failed to load image for editing.'));
      };
      img.src = objectUrl;
    });
  } catch {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('Failed to load image for editing.'));
      img.src = url;
    });
  }
}

function makeCanvas(w: number, h: number): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  return c;
}

/** Centre-crop insets (0..1) to match a target aspect ratio. */
export function centreCropInsets(
  width: number,
  height: number,
  targetAspect: number | null
): { top: number; right: number; bottom: number; left: number } {
  if (!targetAspect || width <= 0 || height <= 0) {
    return { top: 0, right: 0, bottom: 0, left: 0 };
  }
  const current = width / height;
  if (Math.abs(current - targetAspect) < 0.01) {
    return { top: 0, right: 0, bottom: 0, left: 0 };
  }
  if (current > targetAspect) {
    const newW = height * targetAspect;
    const inset = (1 - newW / width) / 2;
    return { top: 0, bottom: 0, left: inset, right: inset };
  }
  const newH = width / targetAspect;
  const inset = (1 - newH / height) / 2;
  return { top: inset, bottom: inset, left: 0, right: 0 };
}

function buildToneLut(tone: PolishTone): Uint8ClampedArray | null {
  if (
    tone.exposure === 0 &&
    tone.brightness === 0 &&
    tone.contrast === 0 &&
    tone.highlights === 0 &&
    tone.shadows === 0
  ) {
    return null;
  }
  const lut = new Uint8ClampedArray(256);
  const exposureFactor = Math.pow(2, tone.exposure / 100);
  const brightnessOffset = (tone.brightness / 100) * 0.5;
  const contrast = 1 + tone.contrast / 100;
  for (let i = 0; i < 256; i++) {
    let t = i / 255;
    t *= exposureFactor;
    t += brightnessOffset;
    const c = clamp01(t);
    const shadowMask = (1 - c) * (1 - c);
    const highlightMask = c * c;
    t += (tone.shadows / 100) * 0.5 * shadowMask;
    t += (tone.highlights / 100) * 0.5 * highlightMask;
    t = (t - 0.5) * contrast + 0.5;
    lut[i] = clamp255(clamp01(t) * 255);
  }
  return lut;
}

function applyCrop(
  source: HTMLImageElement | HTMLCanvasElement,
  cropAspect: CropAspect
): HTMLCanvasElement {
  const w = source instanceof HTMLImageElement ? source.naturalWidth : source.width;
  const h = source instanceof HTMLImageElement ? source.naturalHeight : source.height;
  const opt = CROP_ASPECT_OPTIONS.find((o) => o.id === cropAspect);
  const insets = centreCropInsets(w, h, opt?.ratio ?? null);
  const sx = Math.round(insets.left * w);
  const sy = Math.round(insets.top * h);
  const sw = Math.max(1, Math.round(w * (1 - insets.left - insets.right)));
  const sh = Math.max(1, Math.round(h * (1 - insets.top - insets.bottom)));
  const out = makeCanvas(sw, sh);
  const ctx = out.getContext('2d')!;
  ctx.drawImage(source, sx, sy, sw, sh, 0, 0, sw, sh);
  return out;
}

function applyToneColour(imageData: ImageData, polish: PolishState): void {
  const d = imageData.data;
  const toneLut = buildToneLut(polish.tone);
  const { temperature, tint, saturation } = polish.colour;
  const gainR = 1 + (temperature / 100) * 0.3 + (tint / 100) * 0.12;
  const gainG = 1 - (tint / 100) * 0.24;
  const gainB = 1 - (temperature / 100) * 0.3 + (tint / 100) * 0.12;
  const wbActive = temperature !== 0 || tint !== 0;
  const satFactor = 1 + saturation / 100;
  const colourActive = wbActive || saturation !== 0;

  if (!toneLut && !colourActive) return;

  for (let i = 0; i < d.length; i += 4) {
    let r = d[i];
    let g = d[i + 1];
    let b = d[i + 2];

    if (toneLut) {
      r = toneLut[r];
      g = toneLut[g];
      b = toneLut[b];
    }
    if (wbActive) {
      r = clamp255(r * gainR);
      g = clamp255(g * gainG);
      b = clamp255(b * gainB);
    }
    if (saturation !== 0) {
      const lum = 0.2126 * r + 0.7152 * g + 0.0722 * b;
      r = clamp255(lum + (r - lum) * satFactor);
      g = clamp255(lum + (g - lum) * satFactor);
      b = clamp255(lum + (b - lum) * satFactor);
    }
    d[i] = r;
    d[i + 1] = g;
    d[i + 2] = b;
  }
}

export function buildPreviewSource(img: HTMLImageElement, maxEdge = 1400): HTMLCanvasElement {
  const scale = Math.min(1, maxEdge / Math.max(img.naturalWidth, img.naturalHeight));
  const w = Math.max(1, Math.round(img.naturalWidth * scale));
  const h = Math.max(1, Math.round(img.naturalHeight * scale));
  const c = makeCanvas(w, h);
  const ctx = c.getContext('2d')!;
  ctx.imageSmoothingEnabled = true;
  ctx.drawImage(img, 0, 0, w, h);
  return c;
}

export function renderPolishToCanvas(
  source: HTMLImageElement | HTMLCanvasElement,
  polish: PolishState,
  target: HTMLCanvasElement
): void {
  const cropped = applyCrop(source, polish.cropAspect);
  target.width = cropped.width;
  target.height = cropped.height;
  const ctx = target.getContext('2d', { willReadFrequently: true });
  if (!ctx) return;
  ctx.drawImage(cropped, 0, 0);
  const imageData = ctx.getImageData(0, 0, cropped.width, cropped.height);
  applyToneColour(imageData, polish);
  ctx.putImageData(imageData, 0, 0);
}

export async function exportPolishedBlob(
  sourceUrl: string,
  polish: PolishState,
  quality = 0.92
): Promise<Blob> {
  const img = await loadImage(sourceUrl);
  const canvas = makeCanvas(1, 1);
  renderPolishToCanvas(img, polish, canvas);
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Failed to encode polished image'))),
      'image/jpeg',
      quality
    );
  });
}

/** Grey-world auto white balance → temperature/tint slider deltas. */
export function computeAutoWhiteBalance(
  source: HTMLCanvasElement | HTMLImageElement
): { temperature: number; tint: number } {
  const w = source instanceof HTMLImageElement ? source.naturalWidth : source.width;
  const h = source instanceof HTMLImageElement ? source.naturalHeight : source.height;
  const maxEdge = 256;
  const scale = Math.min(1, maxEdge / Math.max(w, h));
  const sw = Math.max(1, Math.round(w * scale));
  const sh = Math.max(1, Math.round(h * scale));
  const c = makeCanvas(sw, sh);
  const ctx = c.getContext('2d', { willReadFrequently: true });
  if (!ctx) return { temperature: 0, tint: 0 };
  ctx.drawImage(source, 0, 0, sw, sh);
  const data = ctx.getImageData(0, 0, sw, sh).data;

  let sumR = 0;
  let sumG = 0;
  let sumB = 0;
  let n = 0;
  for (let i = 0; i < data.length; i += 4) {
    const lum = 0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2];
    if (lum < 12 || lum > 243) continue;
    sumR += data[i];
    sumG += data[i + 1];
    sumB += data[i + 2];
    n++;
  }
  if (n === 0) return { temperature: 0, tint: 0 };

  const avgR = sumR / n;
  const avgG = sumG / n;
  const avgB = sumB / n;
  const grey = (avgR + avgG + avgB) / 3;
  if (avgR <= 0 || avgG <= 0 || avgB <= 0) return { temperature: 0, tint: 0 };

  const gainR = grey / avgR;
  const gainG = grey / avgG;
  const gainB = grey / avgB;
  const clamp100 = (v: number) => Math.max(-100, Math.min(100, Math.round(v)));
  return {
    temperature: clamp100(((gainR - gainB) / 0.6) * 100),
    tint: clamp100(((1 - gainG) / 0.24) * 100),
  };
}

export function polishIsIdentity(polish: PolishState): boolean {
  const { tone, colour, cropAspect } = polish;
  return (
    cropAspect === 'free' &&
    tone.exposure === 0 &&
    tone.brightness === 0 &&
    tone.contrast === 0 &&
    tone.highlights === 0 &&
    tone.shadows === 0 &&
    colour.temperature === 0 &&
    colour.tint === 0 &&
    colour.saturation === 0
  );
}
